<i18n>
{
  "en": {
    "add": "Add new item",
    "confirm": "Confirm",
    "confirmText":"This action will be permanent and can't be undone",
    "confirmDeleteTitle": "Delete the grid item: {0}",
    "createError": "Error creating a new grid item",
    "delete": "Delete current item",
    "edit": "Edit current item",
    "errorSaveGridItemConfig": "Error saving grid item config",
    "freeText": "Free text",
    "gridItem": "Grid items",
    "gridItemSelect": "Grid item selector",
    "imageType": "Image",
    "menuSection": "Menu section",
    "save": "Save item configuration",
    "saveGridItemConfig": "Grid item config has been successfully saved",
    "social": "Social"
  },
  "es": {
    "add": "Añadir un nuevo elemento",
    "confirm": "Confirmar",
    "confirmDeleteTitle": "Eliminar el elemento de la plantilla: {0}",
    "confirmText":"Esta acción será permanente y no podrá deshacerse",
    "createError": "Error creando un nuevo elemento",
    "delete": "Eliminar elemento actual",
    "edit": "Editar elemento actual",
    "errorSaveGridItemConfig": "Error al guardar la configuración del elemento de la cuadrícula",
    "freeText": "Texto libre",
    "gridItem": "Elementos de la plantilla",
    "gridItemSelect":  "Seleccionar elemento de la plantilla",
    "imageType": "Imagen",
    "menuSection": "Sección del menú",
    "save": "Guardar la configuración del elemento actual",
    "saveGridItemConfig": "Se ha guardado la configuración del elemento de la cuadrícula correctamente",
    "social": "Redes sociales"
  }
}
</i18n>
<template>
  <fragment>
    <v-form class="form">
      <div class="d-flex flex-wrap justify-lg-space-between">
        <div class="title-form-grid">{{ $t('gridItem') }}</div>
        <div>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon class="mr-2" v-on="on" v-bind="attrs" @click="onOpenCreateModal()">
                <v-icon color="grey" large>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('add') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn :disabled="!selectedGridItem" icon class="mr-2" v-on="on" v-bind="attrs"
                     @click="onSaveGridItemConfiguration">
                <v-icon class="icon-form" color="grey" large>mdi-content-save-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('save') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn :disabled="!selectedGridItem" icon class="mr-2" v-on="on" v-bind="attrs"
                     @click="onOpenEditModal">
                <v-icon class="icon-form" color="grey" large>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn :disabled="!selectedGridItem" icon class="mr-2" v-on="on" v-bind="attrs"
                     @click="onDeleteModal">
                <v-icon class="icon-form" color="grey" large>mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>
        </div>
      </div>
      <v-select
        :value="selectedGridItem"
        @change="handleChangeSelect"
        v-if="menuGridItemList.length > 0"
        :items="menuGridItemListOrdered"
        item-text="name"
        return-object
        class="mx-2"
        :label="$t('gridItemSelect')"
      >
        <template v-slot:item="{ item }">
          <div class="d-flex justify-center">
            <div>
              {{ item.name }} -
              <span class="type-option"> {{ optionsType[item.type] }}</span>
              <span class="gridItemRequired"> {{ itemRequired.includes(item.guid) ? "*" : ""}}</span>
            </div>
          </div>
        </template>
        <template v-slot:selection="{ item }">
          <div class="d-flex justify-center">
            <div>{{ item.name }} - <span class="type-option"> {{ optionsType[item.type] }} </span> <span class="gridItemRequired"> {{ itemRequired.includes(item.guid) ? "*" : ""}}</span></div>
          </div>
        </template>
      </v-select>
    </v-form>
    <grid-item-create-edit-modal
      :visible="openModal"
      :menuGridItem="gridItemToInteract"
      :editMode="editMode"
      @cancel="onClose"
      @confirm="onConfirmGridItem"
    />
    <dynameat-confirmation-modal :visible="deleteConfirmationModal"
                                 :title="titleConfirmationModal"
                                 :description="$t('confirmText')" :confirm-text="$t('confirm')"
                                 @confirm="onConfirmDelete" @cancel="onCloseConfirm"/>
  </fragment>
</template>
<script>
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import Component from 'vue-class-component';
import Vue from 'vue';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import { Prop } from 'vue-property-decorator';
import GridItemCreateEditModal from './gridItemCreateEditModal.vue';
import { MenuGridItem } from '../models';

@Component({
  components: { GridItemCreateEditModal }
})
@Components({
  GridItemCreateEditModal,
  DynameatConfirmationModal
})
@MapGetters(['gridConfiguration', 'menuGridItemList', 'selectedGridItem'])
@MapActions(['selectGridItem', 'createMenuGridItem', 'addNotification', 'menuGridItemListRequest',
  'updateGridItemConfigurationRequest', 'editMenuGridItem', 'deleteMenuGridItem', 'selectGridItemConfigurationToNull'])
export default class GridItemList extends Vue {
  @Prop({ Array, default: () => [] }) itemRequired;

  openModal = false;

  editMode = false;

  gridItemSelected = null;

  gridItemToInteract = null;

  deleteConfirmationModal = false;

  get titleConfirmationModal() {
    if (this.gridItemSelected) {
      return this.$t('confirmDeleteTitle', [this.gridItemSelected.name]);
    }
    return '';
  }

  get optionsType() {
    return {
      2: this.$t('menuSection'),
      3: this.$t('image'),
      4: this.$t('freeText'),
      1: this.$t('social'),
    };
  }

  get menuGridItemListOrdered() {
    return this.menuGridItemList.sort((a, b) => a.type - b.type);
  }

  handleChangeSelect(value) {
    this.selectGridItem(value);
    this.gridItemSelected = value;
  }

  async onConfirmDelete() {
    try {
      await this.deleteMenuGridItem(this.gridItemSelected);
      await this.menuGridItemListRequest();
      this.onClose();
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('errorDeletingGridItem'), null, 5000));
    }
  }

  onDeleteModal() {
    this.gridItemToInteract = MenuGridItem.toInstance(this.gridItemSelected.serialize());
    this.deleteConfirmationModal = true;
  }

  onCloseConfirm() {
    this.deleteConfirmationModal = false;
  }

  onOpenCreateModal() {
    this.gridItemToInteract = new MenuGridItem();
    this.openModal = true;
  }

  onOpenEditModal() {
    this.gridItemToInteract = MenuGridItem.toInstance(this.gridItemSelected.serialize());
    this.editMode = true;
    this.openModal = true;
  }

  async onSaveGridItemConfiguration() {
    try {
      await this.updateGridItemConfigurationRequest();
      this.addNotification(createSuccessNotification(this.$t('saveGridItemConfig'), null, 3000));
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('errorSaveGridItemConfig'), null, 5000));
    }
  }

  onClose() {
    this.openModal = false;
    this.editMode = false;
    this.gridItem = null;
    this.gridItemSelected = null;
    this.selectGridItem(null);
    this.deleteConfirmationModal = false;
    this.selectGridItemConfigurationToNull();
  }

  async onConfirmGridItem(menuGridItem) {
    try {
      if (!this.editMode) {
        await this.createMenuGridItem(menuGridItem);
      } else {
        await this.editMenuGridItem(menuGridItem);
      }
      await this.menuGridItemListRequest();
    } catch (err) {
      console.log(err);
      this.addNotification(createErrorNotification(this.$t('createError'), null, 5000));
    } finally {
      this.onClose();
    }
  }
}
</script>
<style scoped>
.form {
  width: 100%;
}

.icon-form {
  font-size: 30px !important;
}

.type-option {
  font-size: 12px;
  color: #6e6e6e;
}

.gridItemRequired { color: crimson}
</style>
